import { styled } from '@linaria/react';
import { isDefined } from 'app-shared/utils';
import { type IconComponent } from 'app-ui/icon';
import { Tooltip } from 'app-ui/primitives/surfaces';
import { MOBILE_VIEWPORT, themeCssVariables, useTheme } from 'app-ui/theme';
import { type ReactNode } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { useIsNavigationDrawerCollapsed } from '@/ui/layout/navigation-drawer/hooks/useIsNavigationDrawerCollapsed';
import { isMobileNavigationDrawerOpenState } from '@/ui/layout/navigation-drawer/states/isMobileNavigationDrawerOpenState';
import { useSetAtomState } from '@/ui/utilities/state/jotai/hooks/useSetAtomState';

// Shared by the link and the button variants so both look the same.
const ITEM_STYLES = `
  align-items: center;
  background: transparent;
  border: none;
  border-radius: ${themeCssVariables.border.radius.md};
  box-sizing: border-box;
  color: ${themeCssVariables.font.color.secondary};
  cursor: pointer;
  display: flex;
  flex: none;
  font-family: inherit;
  font-size: ${themeCssVariables.font.size.md};
  font-weight: ${themeCssVariables.font.weight.medium};
  gap: ${themeCssVariables.spacing[2]};
  height: ${themeCssVariables.spacing[7]};
  outline: none;
  padding: 0 ${themeCssVariables.spacing[2]};
  text-align: left;
  text-decoration: none;
  transition: ${themeCssVariables.clickableElementBackgroundTransition};
  user-select: none;
  width: 100%;

  &[aria-current='page'] {
    background: ${themeCssVariables.background.transparent.light};
    color: ${themeCssVariables.font.color.primary};
  }

  &:focus-visible {
    background: ${themeCssVariables.background.transparent.light};
    color: ${themeCssVariables.font.color.primary};
  }

  &[data-collapsed] {
    justify-content: center;
    padding: 0;
    width: ${themeCssVariables.spacing[8]};
  }

  @media (hover: hover) {
    &:hover {
      background: ${themeCssVariables.background.transparent.light};
      color: ${themeCssVariables.font.color.primary};
    }
  }

  @media (max-width: ${MOBILE_VIEWPORT}px) {
    height: ${themeCssVariables.spacing[11]};
  }
`;

const StyledLink = styled(Link)`
  ${ITEM_STYLES}
`;

const StyledButton = styled.button`
  ${ITEM_STYLES}
`;

const StyledIconContainer = styled.span`
  align-items: center;
  display: flex;
  flex: none;
`;

const StyledLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type NavigationDrawerItemProps = {
  label: string;
  Icon: IconComponent;
  to?: string;
  // Only matches the exact path, so a parent path doesn't light up on every
  // child page.
  end?: boolean;
  onClick?: () => void;
  endContent?: ReactNode;
};

export const NavigationDrawerItem = ({
  label,
  Icon,
  to,
  end = false,
  onClick,
  endContent,
}: NavigationDrawerItemProps) => {
  const theme = useTheme();
  const location = useLocation();
  const isCollapsed = useIsNavigationDrawerCollapsed();
  const setIsMobileNavigationDrawerOpen = useSetAtomState(
    isMobileNavigationDrawerOpenState,
  );

  const isCurrentPage =
    isDefined(to) && isDefined(matchPath({ path: to, end }, location.pathname));

  const handleClick = () => {
    setIsMobileNavigationDrawerOpen(false);
    onClick?.();
  };

  const content = (
    <>
      <StyledIconContainer>
        <Icon
          size={theme.icon.size.md}
          stroke={theme.icon.stroke.sm}
          aria-hidden
        />
      </StyledIconContainer>
      {!isCollapsed && (
        <>
          <StyledLabel>{label}</StyledLabel>
          {endContent}
        </>
      )}
    </>
  );

  const item = isDefined(to) ? (
    <StyledLink
      to={to}
      aria-current={isCurrentPage ? 'page' : undefined}
      aria-label={isCollapsed ? label : undefined}
      data-collapsed={isCollapsed || undefined}
      onClick={handleClick}
    >
      {content}
    </StyledLink>
  ) : (
    <StyledButton
      type="button"
      aria-label={isCollapsed ? label : undefined}
      data-collapsed={isCollapsed || undefined}
      onClick={handleClick}
    >
      {content}
    </StyledButton>
  );

  return (
    <Tooltip content={label} side="right" disabled={!isCollapsed}>
      {item}
    </Tooltip>
  );
};
